import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import { PopoverModule, Popover } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';

import { ModuleService, AppModule } from '@layout/service/module.service';
import { AuthService } from '@auth/services/auth.service';

@Component({
  selector: 'app-module-selector',
  standalone: true,
  imports: [CommonModule, PopoverModule, ButtonModule, RippleModule],
  template: `
    <div class="module-selector-wrapper">
      <!-- Botón Gatillo: Muestra el módulo actual -->
      <button
        pButton
        pRipple
        type="button"
        class="p-button-text p-button-plain current-module-btn"
        (click)="op.toggle($event)"
      >
        <i [class]="activeModule()?.icon || 'pi pi-th-large'" class="mr-2 text-primary text-2xl"></i>
        <span class="font-bold text-xl text-900">{{ activeModule()?.label || 'Módulos' }}</span>
        <i class="pi pi-chevron-down ml-2 text-sm text-500"></i>
      </button>

      <!-- Panel de Módulos (Grid) -->
      <p-popover #op [style]="{ width: '380px' }">
        <!-- Alto acotado y scroll: en pantallas de poca altura la rejilla se
             cortaba y los últimos módulos quedaban fuera de alcance. -->
        <div class="p-3 module-popover-body">
          <div class="text-sm font-bold text-500 mb-4 uppercase tracking-wider px-2">Mis Módulos</div>
          <div class="grid grid-cols-3 gap-3">
            @for (module of visibleModules(); track module.id) {
              <button
                type="button"
                class="module-grid-item"
                [class.active]="moduleService.isModuleSelected(module.id)"
                (click)="onModuleClick(module, op)"
                pRipple
              >
                <div class="icon-box">
                  <i [class]="module.icon" style="font-size: 2.5rem"></i>
                </div>
                <span class="module-name">{{ module.label }}</span>
                @if (moduleService.isModuleSelected(module.id)) {
                  <div class="active-indicator"></div>
                }
              </button>
            }
          </div>
        </div>
      </p-popover>
    </div>
  `,
  styles: [
    `
      .module-selector-wrapper {
        display: none;
      }

      /* El scroll de .module-popover-body vive en src/assets/_overrides.scss:
         el contenido del popover cuelga de body y el CSS encapsulado no lo alcanza. */

      @media (min-width: 992px) {
        .module-selector-wrapper {
          display: block;
          padding-left: 1.5rem;
          margin-left: 1.5rem;
          border-left: 1px solid var(--surface-border);
        }
      }

      .current-module-btn {
        padding: 0.75rem 1rem;
        border-radius: 10px;
        transition: background 0.2s;
        display: flex;
        align-items: center;

        &:hover {
          background: var(--surface-hover);
        }
      }

      .module-grid-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 1.25rem 0.75rem;
        border-radius: 14px;
        cursor: pointer;
        transition: all 0.2s ease;
        position: relative;
        text-align: center;
        background: transparent;
        border: none;
        width: 100%;

        .icon-box {
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
          background: var(--surface-100);
          margin-bottom: 0.75rem;
          color: var(--text-color-secondary);
          font-size: 1.75rem;
          transition: all 0.2s;
        }

        .module-name {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-color);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        &:hover {
          background: var(--surface-hover);
          .icon-box {
            background: var(--primary-100);
            color: var(--primary-color);
            transform: translateY(-3px);
            box-shadow: 0 4px 12px color-mix(in srgb, var(--primary-color) 20%, transparent);
          }
        }

        &.active {
          background: var(--primary-50);
          .icon-box {
            background: var(--primary-color);
            color: #ffffff;
            box-shadow: 0 4px 12px color-mix(in srgb, var(--primary-color) 40%, transparent);
          }
          .module-name {
            color: var(--primary-color);
            font-weight: 700;
          }
        }
      }

      .active-indicator {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 10px;
        height: 10px;
        background: var(--primary-color);
        border-radius: 50%;
        border: 2px solid var(--surface-card);
      }
    `,
  ],
})
export class AppModuleSelectorComponent {
  moduleService = inject(ModuleService);
  private authService = inject(AuthService);

  activeModule = computed(() =>
    this.moduleService.visibleModules().find((m) => this.moduleService.isModuleSelected(m.id))
  );

  isAdmin = computed(() => this.authService.isAdmin());

  visibleModules = computed(() => {
    return this.moduleService.visibleModules().filter((module) => {
      if (module.id === 'admin') return this.isAdmin();
      return module.visible !== false;
    });
  });

  onModuleClick(module: AppModule, op: Popover): void {
    this.moduleService.selectModule(module.id);
    op.hide();
  }
}
